import type { Page } from '../../types';
import { getColumns, getKeys, getRows } from '../../../components/form/CoreCphConsolidatedInput';

const costPerHireDelegated: Page[] = [
  {
    pageType: 'information',
    title: 'Before you start',
    name: 'before-you-start',
    continueConfirmation: 'I have read and understand the guidance',
    description: `
      <strong>Overview</strong><br>
      Cost Per Hire is the mean average cost per successful appointment made in the specified reporting period. It is vital that departments understand the cost implications of their recruitment processes. To establish Cost Per Hire, departments must understand what standard costs are involved in a recruitment campaign. These can be classified as either internal or external costs. Costs should be included that arise from successful recruitment campaigns, as well as those that are unsuccessful and do not result in an appointment. It is also necessary to understand the number of successful appointments made in the reporting period. In addition, the number of failed campaigns that did not result in an appointment should be reported to provide insight into the volume of recruitment activity that does not yield a return on investment. Cost Per Hire measurements should be classified and monitored according to the grade structure of the department.<br><br>

      <strong>Methodology</strong><br>
      The basic calculation for Cost Per Hire is:<br>
      CPH = (Internal Costs + External Costs)/Successful Candidates<br>
      "Successful candidate" is defined as candidates receiving a formal offer in the reporting window.<br>
      Costs should reflect either the actual spend incurred during the reporting window or a prorated portion of rolling costs to represent one quarter's expenditure.<br>
      We acknowledge that reporting costs and successful candidates within the same window can create mismatches; please use the provided comment boxes to contextualise any resulting anomalies. As we progress with this metric we will view CPH over a longer period of time.<br><br>

      To work out the mean average, it is necessary to divide the combined internal and external costs by the number of appointments in the reporting period. To establish Cost Per Hire, departments must understand what standard costs are involved in a recruitment campaign. These can be classified as either internal or external costs. Internal costs are likely to be: staffing time and resource planning, sifting, interview time (measured by representative sampling); departmental recruitment team budget (prorated to align with the specified reporting period); resource attributed to handling candidate enquiries. External costs are likely to be: advertising budget; Application Tracking System (ATS) contract; external search firm and outsourcing costs e.g. SCS assessments.<br><br>

      What makes up the internal and external costs will vary by department. However, a clear explanation of what has been factored into the cost per hire calculation must be provided. Where costs are variable e.g. defined by the number of recruitment campaigns or applicable grade, it will be necessary to ensure the cost is multiplied by the number of recruitment campaigns undertaken in the reporting period.<br><br>

      For example, staffing time and resources for an HEO campaign are measured to cost roughly GBP 500. During the reporting period there were 30 HEO recruitment campaigns. The internal staffing and resource cost for HEO campaigns in the reporting period would be considered to be GBP 500 x 30 = GBP 15,000. Where costs are contractually fixed over a period of years, these costs may be prorated to align with the duration of the reporting period.<br><br>

      Cost Per Hire reporting is equally about providing a narrative context as to the costs factored into the calculation, to ensure benchmarking is to a comparable standard, as it is about providing the actual costing figure.<br><br>

      Given the complexity of calculating Cost Per Hire, it is not anticipated that departments will have a complete and accurate measurement ready for initial scheduled reports. Departments should provide context as to the inclusions and confidence in their Cost Per Hire reporting, and a timeframe for improving upon that where necessary, for example where estimates or gaps in known costs exist.
    `,
  },
  {
    pageType: 'question',
    title: 'Delegated Recruitment',
    name: 'cost-per-hire-delegated-internal',
    description: `
      When calculating your figures, please distinguish between Internal Costs (e.g., the value of staff time spent sifting, interviewing, etc) and External Costs (e.g., third-party advertising, search firm fees, etc).<br><br>
      For each cost category, please use the tick boxes to indicate the extent to which these expenses are captured in your data. Finally, provide the mean average internal and external cost per successful appointment made during this reporting period for both internal and external streams.
    `,
    questions: [
      {
        questionType: 'coreCostInteralInput',
        title: 'Internal Cost Inclusions',
        name: 'cph-delegated-internal-inclusions',
        hideOptionalLabel: true,
        detailsHeading: 'More information',
        detailsContent:
          'Internal costs are likely to be: staffing time and resource planning, sifting, interview time (measured by representative sampling); departmental recruitment team budget (prorated to align with the specified reporting period); resource attributed to handling candidate enquiries.',
        rules: {
          required: false,
        },
      },
      {
        questionType: 'coreCostDelegatedInput',
        title: 'Average internal cost per hire for delegated grades',
        name: 'cph-delegated-internal',
        detailsHeading: 'More information',
        detailsContent:
          'Failed campaigns in the reporting period: Where no appointable candidate was identified or all appointable candidates withdraw before starting in post',
        rules: {
          required: true,
          decimal: 'decimal(2)',
        },
      },
      {
        questionType: 'textarea',
        title: 'Comments and observations',
        name: 'cph-delegated-internal-comments',
        hint: 'Please outline any factors that may explain quarter-on-quarter change or influence these reported figures.',
        rules: {
          required: true,
        },
      },
    ],
  },
  {
    pageType: 'question',
    title: 'Delegated Recruitment',
    name: 'cost-per-hire-delegated-external',
    description: `
      When calculating your figures, please distinguish between Internal Costs (e.g., the value of staff time spent sifting, interviewing, etc) and External Costs (e.g., third-party advertising, search firm fees, etc).<br><br>
      For each cost category, please use the tick boxes to indicate the extent to which these expenses are captured in your data. Finally, provide the mean average internal and external cost per successful appointment made during this reporting period for both internal and external streams.
    `,
    questions: [
      {
        questionType: 'coreCostExternalInput',
        title: 'External Cost Inclusions',
        name: 'cph-delegated-external-inclusions',
        hideOptionalLabel: true,
        detailsHeading: 'More information',
        detailsContent:
          'External costs are likely to be: advertising budget; Application Tracking System (ATS) contract; external search firm and outsourcing costs e.g. SCS assessments.',
        rules: {
          required: false,
        },
      },
      {
        questionType: 'coreCostDelegatedInput',
        title: 'Average external cost per hire for delegated grades',
        name: 'cph-delegated-external',
        detailsHeading: 'More information',
        detailsContent:
          'Failed campaigns in the reporting period: Where no appointable candidate was identified or all appointable candidates withdraw before starting in post',
        rules: {
          required: true,
          decimal: 'decimal(2)',
        },
      },
      {
        questionType: 'textarea',
        title: 'Comments and observations',
        name: 'cph-delegated-external-comments',
        hint: 'Please outline any factors that may explain quarter-on-quarter change or influence these reported figures.',
        rules: {
          required: true,
        },
      },
    ],
  },
];

export default costPerHireDelegated;
